<script setup>
import { spring } from 'motion';
import { animate } from 'motion/mini';
import { nextTick, ref, watch } from 'vue';

/**
 * @typedef {object} TransitionCollapseProps
 * @property {boolean} [isOpen=false] - Whether the collapse is open
 * @property {number} [openDuration=1] - Duration of the open animation
 * @property {number} [closeDuration=0.8] - Duration of the close animation
 * @property {number} [openBounce=0.2] - Bounce of the open animation
 * @property {number} [closeBounce=0.2] - Bounce of the close animation
 */

/**
 * @type {TransitionCollapseProps}
 */
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  openDuration: {
    type: Number,
    default: 1,
  },
  closeDuration: {
    type: Number,
    default: 0.8,
  },
  openBounce: {
    type: Number,
    default: 0.2,
  },
  closeBounce: {
    type: Number,
    default: 0.2,
  },
});

const contentRef = ref(null);
const isRender = ref(props.isOpen);
const isAnimating = ref(false);

watch(
  () => props.isOpen,
  async (newValue, oldValue) => {
    if (newValue === oldValue) return;

    if (newValue) {
      isRender.value = true;
      isAnimating.value = true;

      await nextTick();
      const el = contentRef.value;
      if (el) {
        el.style.visibility = 'hidden';
        el.style.height = 'auto';
        const height = el.scrollHeight;

        el.style.height = 0;

        await animate(
          el,
          { height: `${height}px`, visibility: 'visible' },
          {
            type: spring,
            duration: props.openDuration,
            bounce: props.openBounce,
          }
        );

        el.style.height = 'auto';
        isAnimating.value = false;
      }
    } else {
      isAnimating.value = true;
      const el = contentRef.value;
      if (el) {
        await animate(
          el,
          { height: `0px`, visibility: 'visible' },
          {
            type: spring,
            duration: props.closeDuration,
            bounce: props.closeBounce,
          }
        );
        isRender.value = false;
        isAnimating.value = false;
      }
    }
  }
);
</script>

<template>
  <div v-if="isRender">
    <div
      ref="contentRef"
      :class="{ 'overflow-hidden': isAnimating }"
      data-component="CsCollapse"
    >
      <slot />
    </div>
  </div>
</template>
